import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { collection, onSnapshot } from 'firebase/firestore';
import { initializeFirebase } from '@/lib/firebase';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Search } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from 'lucide-react';

interface Token {
  address: string;
  name: string;
  symbol: string;
  owner: string;
  chainId?: number | null;
  chainName?: string | null;
}

export function TokenList() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChainId, setSelectedChainId] = useState<'all' | string>('all');

  const uniqueChains = Array.from(
    new Map(
      tokens
        .filter((token) => token.chainId != null)
        .map((token) => [
          token.chainId as number,
          {
            chainId: token.chainId as number,
            chainName: token.chainName || `Chain ${token.chainId}`,
          },
        ])
    ).values()
  );

  // Load tokens from Firestore
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isMounted = true;

    const init = async () => {
      try {
        const { db } = await initializeFirebase();
        const tokensRef = collection(db, 'tokens');

        unsubscribe = onSnapshot(
          tokensRef,
          (snapshot) => {
            if (!isMounted) return;

            const docs = snapshot.docs.map((doc) => {
              const data = doc.data() as any;
              return {
                address: data.address || '',
                name: data.name || '',
                symbol: data.symbol || '',
                owner: data.owner || '',
                chainId: typeof data.chainId === 'number' ? data.chainId : null,
                chainName: data.chainName || null,
                createdAt: data.createdAt ? new Date(data.createdAt) : null,
              } as Token & { createdAt?: Date | null };
            });

            // Sort newest first when createdAt is available
            docs.sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0;
              return b.createdAt.getTime() - a.createdAt.getTime();
            });

            setTokens(docs.map(({ createdAt, ...rest }) => rest));
            setIsLoading(false);
          },
          (error) => {
            console.error('Error loading tokens from Firestore:', error);
            if (isMounted) setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('Failed to initialize Firebase for TokenList:', error);
        if (isMounted) setIsLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Filter tokens based on search term
  const filteredTokens = tokens
    .filter((token) => {
      if (selectedChainId === 'all') return true;
      if (token.chainId == null) return false;
      return String(token.chainId) === selectedChainId;
    })
    .filter(token => 
      token.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      token.owner.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Export token list as CSV
  const exportToCSV = () => {
    const headers = ['Address', 'Name', 'Symbol', 'Owner'];
    const csvContent = [
      headers.join(','),
      ...tokens.map(token => 
        [token.address, token.name, token.symbol, token.owner].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'token_list.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CardTitle>Token List</CardTitle>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>List of all tokens created through the platform</p>
                  <p>View token details and monitor token activity</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <CardDescription>View and manage created tokens</CardDescription>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>Export</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Export token list as CSV file</p>
                </TooltipContent>
              </Tooltip>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tokens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Search by token address, name, symbol, or owner address</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {uniqueChains.length > 0 && (
              <div className="flex flex-col text-sm">
                <span className="text-muted-foreground mb-1" id="token-network-filter-label">Network</span>
                <select
                  value={selectedChainId}
                  onChange={(e) => setSelectedChainId(e.target.value)}
                  className="border border-input bg-background rounded-md px-2 py-1 text-sm"
                  aria-labelledby="token-network-filter-label"
                  title="Filter tokens by blockchain network"
                >
                  <option value="all">All</option>
                  {uniqueChains.map((chain) => (
                    <option key={chain.chainId} value={String(chain.chainId)}>
                      {chain.chainName ?? `Chain ${chain.chainId}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center space-x-2">
                      <span>Address</span>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Token contract address</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center space-x-2">
                      <span>Name</span>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Token name as specified during creation</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center space-x-2">
                      <span>Symbol</span>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Token trading symbol</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center space-x-2">
                      <span>Owner</span>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Wallet address of token owner</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      Loading tokens...
                    </TableCell>
                  </TableRow>
                ) : filteredTokens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No tokens found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTokens.map((token, index) => (
                    <TableRow key={token.address}>
                      <TableCell className="font-mono">{token.address}</TableCell>
                      <TableCell>{token.name}</TableCell>
                      <TableCell>{token.symbol}</TableCell>
                      <TableCell className="font-mono">{token.owner}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
